/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const StyleMarqueSegment = require('../types/StyleMarqueSegment')
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CMarqueAngleAncetre = require('./CMarqueAngleAncetre')
const StyleMarqueAngle = require('../types/StyleMarqueAngle')
module.exports = CMarqueAngleGeometrique

/**
 * Classe représentant une marque d'angle non orienté.
 * @constructor
 * @extends CMarqueAngleAncetre
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si élément final de construction.
 * @param {mtg32.Color} couleur : La couleur.
 * @param {Boolean} masque : true si l'objet est masqué.
 * @param {mtg32.StleTrait} style : Donne le style trait utilisé.
 * @param {StyleMarqueAngle} styleMarque : Donne le style de la marque.
 * @param {Float} rayon : Le rayon de l'arc.
 * @param {CPt} a : Le premier point
 * @param {CPt} o : Le point sommet de l'angle définissant l'arc.
 * @param {CPt} b : Le troisième point.
 * @returns {CMarqueAngleGeometrique}
 */
function CMarqueAngleGeometrique (listeProprietaire, impProto, estElementFinal, couleur, masque,
    style, styleMarque, rayon, a, o, b){
  if (arguments.length === 0) return; // Ajout version 4.9.9.4
  if (arguments.length === 1) CMarqueAngleAncetre.call(this, listeProprietaire);
  else {
    CMarqueAngleAncetre.call(this, listeProprietaire, impProto, estElementFinal, couleur,
      masque, style, styleMarque, rayon);
    this.a = a;
    this.o = o;
    this.b = b;
  }
}
CMarqueAngleGeometrique.prototype = new CMarqueAngleAncetre();
CMarqueAngleGeometrique.prototype.constructor = CMarqueAngleGeometrique;
CMarqueAngleGeometrique.prototype.superClass = "CMarqueAngleAncetre";
CMarqueAngleGeometrique.prototype.className = "CMarqueAngleGeometrique";

CMarqueAngleGeometrique.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.a);
  var ind2 = listeSource.indexOf(this.o);
  var ind3 = listeSource.indexOf(this.b);
  var ind4 = listeSource.indexOf(this.impProto);
  return new CMarqueAngleGeometrique(listeCible, listeCible.get(ind4, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.masque, this.style, this.styleMarque, this.rayon,
    listeCible.get(ind1, "CPt"), listeCible.get(ind2, "CPt"),listeCible.get(ind3, "CPt"));
};
CMarqueAngleGeometrique.prototype.ajouteAntecedents = function(liste) {
  liste.add(this.a);
  liste.add(this.o);
  liste.add(this.b);
};
CMarqueAngleGeometrique.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CMarqueAngleAncetre.prototype.dependDe.call(this, p)
    || this.a.dependDe(p) || this.o.dependDe(p) || this.b.dependDe(p));
};
CMarqueAngleGeometrique.prototype.dependDePourBoucle = function(p) {
  return ((p === this) || this.a.dependDePourBoucle(p) || this.o.dependDePourBoucle(p) || this.b.dependDePourBoucle(p));
};
CMarqueAngleGeometrique.prototype.positionne = function(infoRandom, dimfen) {
  this.existe = this.o.existe && this.a.existe && this.b.existe && !(this.o.presqueEgal(this.a)) && !(this.o.presqueEgal(this.b));
  if (!this.existe) return;
  this.centre_x = this.o.x;
  this.centre_y = this.o.y;
  this.u.setVecteur(this.o,this.a);
  this.v.setVecteur(this.o,this.b);
  this.ang1rad = this.u.angleRad();
  this.ang2rad = this.v.angleRad();
  if (kernel.zeroAngle(this.ang1rad - this.ang2rad)) {
    this.existe = false;
    return;
  }
  if (kernel.testAngleDroit(this.ang1rad,this.ang2rad)) {
    this.prepareAngleDroit();
    this.point2.positionne(infoRandom, dimfen);
    this.pointc.positionne(infoRandom, dimfen);
    this.segment1.positionne(infoRandom, dimfen);
    this.marqueSegment.donneStyle(this.style);
    this.marqueSegment.donneCouleur(this.couleur);
    switch (this.styleMarque) {
      case StyleMarqueAngle.marqueSimple1Trait:
      case StyleMarqueAngle.marquePleine1Trait:
        this.marqueSegment.donneMotif(StyleMarqueSegment.marqueSimple);
        this.marqueSegment.positionne(false, dimfen);
        break;
      case StyleMarqueAngle.marqueSimple2Traits:
      case StyleMarqueAngle.marquePleine2Traits:
        this.marqueSegment.donneMotif(StyleMarqueSegment.marqueDouble);
        this.marqueSegment.positionne(false, dimfen);
        break;
      case StyleMarqueAngle.marqueSimple3Traits:
      case StyleMarqueAngle.marquePleine3Traits:
        this.marqueSegment.donneMotif(StyleMarqueSegment.marqueTriple);
        this.marqueSegment.positionne(false, dimfen);
        break;
      case StyleMarqueAngle.marqueSimpleCroix:
      case StyleMarqueAngle.marquePleineCroix:
        this.marqueSegment.donneMotif(StyleMarqueSegment.marqueCroix);
        this.marqueSegment.positionne(false, dimfen);
        break;
      default :
        this.marqueSegment.existe = false;
    }
    this.angleDroit = true;
  } else this.angleDroit = false;
  CMarqueAngleAncetre.prototype.positionne.call(this, infoRandom, dimfen);
};
/**
 * Renvoie le path servant à créer le svg element associé.
 * @param {Float} mesang : L'angle utilisé.
 * @returns {String}
 */
CMarqueAngleGeometrique.prototype.path = function(mesang) {
  var u1,v1,xo,yo,xf,yf;
  u1 = new Vect();
  this.u.vecteurColineaire(this.rayon, u1);
  v1 = new Vect();
  this.v.vecteurColineaire(this.rayon, v1);
  xo = this.centre_x + u1.x;
  yo = this.centre_y + u1.y;
  xf = this.centre_x + v1.x;
  yf = this.centre_y + v1.y;
  var sweepflag = (mesang < 0) ? "1 " : "0 ";
  return "M " + xo.toString() + " " + yo + "A" +  this.rayon + ","
    + this.rayon + " 0 0 " + sweepflag + xf.toString() +"," + yf.toString();
};
CMarqueAngleGeometrique.prototype.trace = function(svg, couleurFond, transparence) {
  // if (this.a.horsEcran || this.o.horsEcran || this.b.horsEcran) return;
  var g = this.createg(svg,couleurFond,transparence);
  g.setAttribute("id",this.id);
  svg.appendChild(g);
  this.g = g;
};
CMarqueAngleGeometrique.prototype.update = function(svg, couleurFond, transparence) {
  // if (this.a.horsEcran || this.o.horsEcran || this.b.horsEcran) return;
  var oldg = this.g;
  var g = this.createg(svg,couleurFond,transparence);
  svg.replaceChild(g, oldg);
  g.setAttribute("id",this.id);
  this.g = g;
};
CMarqueAngleGeometrique.prototype.remplacePoint = function(ancienPoint, nouveauPoint) {
  if (this.a === ancienPoint) this.a = nouveauPoint;
  if (this.b === ancienPoint) this.b = nouveauPoint;
  if (this.o === ancienPoint) this.o = nouveauPoint;
};

/**
 *
 * Renvoie true si le nom de la valeur commence par la chaîne st
 * @param st
 * @return
 */
CMarqueAngleGeometrique.prototype.nomCommencePar = function(st){
  if (CValDyn.prototype.nomCommencePar.call(this, st)) return true;
  var ch = this.b.nom + this.o.nom + this.a.nom;
  return ch.indexOf(st) === 0;
}

CMarqueAngleGeometrique.prototype.read = function(inps, list){
  CMarqueAngleAncetre.prototype.read.call(this, inps, list);
  var ind1 = inps.readInt();
  var ind2 = inps.readInt();
  var ind3 = inps.readInt();
  this.a = list.get(ind1,"CPt");
  this.o = list.get(ind2,"CPt");
  this.b = list.get(ind3,"CPt");
};
CMarqueAngleGeometrique.prototype.write = function(oups, list){
  CMarqueAngleAncetre.prototype.write.call(this, oups, list);
  var ind1 = list.indexOf(this.a);
  oups.writeInt(ind1);
  var ind2 = list.indexOf(this.o);
  oups.writeInt(ind2);
  var ind3 = list.indexOf(this.b);
  oups.writeInt(ind3);
};



// WEBPACK FOOTER //
// ./src/objets/CMarqueAngleGeometrique.js